import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  ApolloProvider,
  useApolloClient,
  DefaultContext,
  useQuery,
  QueryOptions,
  OperationVariables,
  MutationOptions,
  useMutation,
  DocumentNode,
  MutationHookOptions,
  QueryHookOptions,
  FetchResult,
  ApolloError,
  ApolloQueryResult,
  gql,
} from '@apollo/client';
import {
  LoginInput,
  loginMutation,
  logOutMutation,
  meQuery,
  OrgAuthConfigPublicFragment,
  OrgAuthConfigType,
  OrgPublicFragment,
  orgPublicQuery,
  refreshTokenMutation,
  registerUserForAuthMutation,
  Role,
} from './verid-api-types';
import { Box, Button, Dialog, Grid, Paper, Typography, TextField, MenuItem, DialogTitle, DialogContent } from '@mui/material';
import { blankUserState, UserContextUtils, UserState } from './user-context';
import { createContext, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContextProps, AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { Util } from '../../util';
import jwt_decode from 'jwt-decode';
import { onError } from '@apollo/client/link/error';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import {
  LoginMutationStatement,
  LogOutMutationStatement,
  MeQueryStatement,
  RefreshTokenMutationStatement,
  RegisterUserForAuthStatement,
} from './person-auth-gql';
import { TokenRefreshLink } from './token-refresh-link';
import { ClientLogger } from '../client-logger';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { SIMULATION_ID_QUERY_PARAM, veridLoginUI } from 'csa-shared-lib';
import { getRoleAndIdFromURLOrStorage, JsonWebToken, setTokenHolder, writeRoleAndIdToStorage } from '../../components';
import { Loading } from '../../widgets';
import { orgPublicQueryGQL } from './use-org-service';
import { SEVERITY, ToastDispatchContext } from '../../toast/Toast';

const DEBUG = true;

const decodeToken = (token: string) => {
  return jwt_decode(token) as JsonWebToken;
};

type MultiAuthTypes = 'passport' | 'cognito' | null;
interface IMultiAuthObject {
  mode: MultiAuthTypes;
}
interface INoAuthObject extends IMultiAuthObject {
  mode: null;
}
interface ICognitoAuthObject extends IMultiAuthObject {
  mode: 'cognito';
  access_token: string;
  user_payload: User;
}
interface IPassportAuthObject extends IMultiAuthObject {
  mode: 'passport';
  access_token: string;
}
type MultiAuthObject = INoAuthObject | ICognitoAuthObject | IPassportAuthObject;

interface ExtendedContext extends DefaultContext {
  noAuth?: boolean;
}
export interface IoidcConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  logout_uri: string;
  response_type: 'code';
  scope: 'email openid phone';
  userStore: any;
}

interface IMultiAuthContext {
  authObject: MultiAuthObject;

  mode: MultiAuthTypes;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userState: UserState;
  $: UserContextUtils; // reorg this

  // Authentication Methods
  cognitoSignIn: () => Promise<boolean>;
  orgId: string;
  role: Role;
  authConfig: OrgAuthConfigPublicFragment;
  passportSignIn: (
    username: string | null,
    password: string | null,
    orgId: string | null,
    authConfigId: string | null,
    role: string | null
  ) => Promise<FetchResult<loginMutation>>;
  globalSignOut: () => Promise<void>;
  getPersonOrgId: () => string | null;

  // Utility
  refreshMe: () => Promise<meQuery['me']>;
  /**
   * This should never be used outside of the Cognito Logout Callback
   * @ignore
   * @deprecated
   * @link https://github.com/nwtoht-ca/csa/pull/3480#discussion_r2127374258
   */
  cognitoAuthObject: AuthContextProps;
  areTokensExpired: () => boolean;
  refreshTokens: () => Promise<string | null>;
}

function convertOrgAuthConfigPublicFragmentToIoidcConfig(authConfig: OrgAuthConfigPublicFragment): IoidcConfig {
  if (authConfig.authConfigType === OrgAuthConfigType.AWS_COGNITO && (!authConfig.redirectUrl || !authConfig.clientId)) {
    throw new Error('Invalid OIDC configuration. Missing redirectUrl or clientId.');
  }
  const resp: IoidcConfig = {
    authority: authConfig.redirectUrl || 'missing',
    client_id: authConfig.clientId || 'missing',
    redirect_uri: `${window.location.origin}/login_callback`,
    logout_uri: `${window.location.origin}/logout_callback`,
    response_type: 'code',
    scope: 'email openid phone',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  };
  DEBUG && ClientLogger.debug('convertOrgAuthConfigPublicFragmentToIoidcConfig', `converted config`, { authConfig, resp });
  return resp;
}

const MultiAuthContext = createContext<IMultiAuthContext | null>(null);
let GloballyAccessibleMultiAuthContext: IMultiAuthContext | null = null;
export const GetGloballyAccessibleMultiAuthContext = () => {
  return GloballyAccessibleMultiAuthContext as Readonly<IMultiAuthContext | null>;
};

const AuthenticationHeader = new ApolloLink((operation, forward) => {
  const context = operation.getContext() as ExtendedContext;
  if (context.noAuth) {
    return forward(operation);
  }
  if (GloballyAccessibleMultiAuthContext === null) {
    throw new Error('Query was made outside the context of `MultiAuthProvider`.');
  }
  const authObject = GloballyAccessibleMultiAuthContext.authObject;
  if (authObject.mode === 'cognito' || authObject.mode === 'passport') {
    operation.setContext({
      ...context,
      headers: { authorization: `Bearer ${authObject.access_token}`, 'x-authentication-mode': authObject.mode },
    });
  }
  return forward(operation);
});

const ErrorLink = onError(({ graphQLErrors, networkError, response, operation, forward }) => {
  if (graphQLErrors) {
    for (const { extensions, message, locations, path } of graphQLErrors) {
      if (extensions?.code === 'UNAUTHENTICATED') {
      }
    }
  }
});

const veridUrl = Util.getEnvVar('REACT_APP_VERID_API', true);
const trl = new TokenRefreshLink();
const clientWithAuth = new ApolloClient({
  link: ApolloLink.from([
    trl,
    AuthenticationHeader,
    ErrorLink,
    createHttpLink({
      uri: veridUrl,
      credentials: 'include',
    }),
  ]),
  cache: new InMemoryCache({ typePolicies: { Form: { keyFields: ['name'] } } }),
  connectToDevTools: true,
});

DEBUG && ClientLogger.debug('HybridApolloClient', 'clientWithAuth', { url: veridUrl });

const clientWithoutAuth = new ApolloClient({
  link: ApolloLink.from([
    ErrorLink,
    createHttpLink({
      uri: Util.getEnvVar('REACT_APP_VERID_API'),
      credentials: 'include',
    }),
  ]),
  cache: new InMemoryCache({ typePolicies: { Form: { keyFields: ['name'] } } }),
  connectToDevTools: true,
});

interface IMultiAuthProvider {
  children: ReactElement | ReactElement[] | undefined;
  authConfig: OrgAuthConfigPublicFragment;
  orgId: string;
  role: Role;
}
const MultiAuthProviderInner = ({ orgId, role, children, authConfig }: IMultiAuthProvider) => {
  const oidcConfig: IoidcConfig = convertOrgAuthConfigPublicFragmentToIoidcConfig(authConfig);
  // External hooks
  const cognitoAuth = useAuth();
  const client = useApolloClient();
  const errorHandler = useErrorHandling('MultiAuthProviderInner');
  // Internal state management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authObject, setAuthObject] = useState<MultiAuthObject>({ mode: null });
  const [userState, setUserState] = useState<UserState>(blankUserState);
  const inSimulator = isInSimulator();

  useEffect(() => {
    const verdict =
      !isLoading && !cognitoAuth.isLoading && cognitoAuth.isAuthenticated && !!cognitoAuth.user && authObject.mode !== 'cognito';
    DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'useEffect', { oidcConfig, cognitoAuth, verdict });
    if (verdict) {
      setAuthObject({
        mode: 'cognito',
        access_token: cognitoAuth.user!.access_token,
        user_payload: cognitoAuth.user!,
      });
    }
  }, [isLoading, cognitoAuth.isLoading, cognitoAuth.isAuthenticated, cognitoAuth.user]);

  useEffect(() => {
    if (cognitoAuth.error) {
      console.error('Cognito Auth Error:', cognitoAuth.error);
      setError(cognitoAuth.error.message);
    }
  }, [cognitoAuth.error]);

  // This event is fired after signing in using Cognito.
  useEffect(() => {
    return cognitoAuth.events.addUserLoaded(async (user: User) => {
      DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'cognitoAuth.events.addUserLoaded', { user, oidcConfig, cognitoAuth });
      setIsLoading(true);

      const localAuthObject: ICognitoAuthObject = {
        mode: 'cognito',
        access_token: user.access_token,
        user_payload: user,
      };
      setTokenHolder(user.access_token);

      if (!user.profile['custom:orgId'] || typeof user.profile['custom:orgId'] !== 'string') {
        throw new Error('User profile does not contain a valid `custom:orgId` attribute.');
      } else {
        if (user.profile['custom:orgId'] !== orgId) {
          throw new Error(`User profile orgId ${user.profile['custom:orgId']} does not match expected orgId ${orgId}.`);
        }
      }

      if (!user.profile['custom:role'] || typeof user.profile['custom:role'] !== 'string') {
        throw new Error('User profile does not contain a valid `custom:role` attribute.');
      } else {
        if (user.profile['custom:role'] !== role) {
          throw new Error(`User profile role ${user.profile['custom:role']} does not match expected role ${role}.`);
        }
      }

      try {
        const response = await client.mutate<registerUserForAuthMutation>({
          mutation: RegisterUserForAuthStatement,
          context: {
            noAuth: true,
            headers: { authorization: `Bearer ${localAuthObject.access_token}`, 'x-authentication-mode': 'cognito' as MultiAuthTypes },
          },
        });
        DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'registerUserForAuth mutation response', { response, localAuthObject });
        const data = response.data?.registerUserForAuth;
        if (!data) {
          throw new Error('No data returned from registerUserForAuth mutation.');
        }
        setUserState({
          me: data,
          isLoggedIn: true,
          ready: true,
        });
        setAuthObject(localAuthObject);
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    });
  }, []);

  const handleCognitoSignOut = async () => {
    if (authObject.mode !== 'cognito') {
      throw new Error('Is not signed in via Cognito.');
    }
    // There is no `state` parameter so we cannot redirect the user back to the current page without using `sessionStorage`.
    // Using `auth.signoutRedirect` without the `extraQueryParams` doesn't work.
    // See https://github.com/authts/oidc-client-ts/issues/1385#issuecomment-2236124145
    // and https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
    // We must direct pass `client_id` and `logout_uri` as query params.
    if (oidcConfig === undefined) {
      throw new Error('OIDC configuration is not set.');
    }
    if (!cognitoAuth) {
      throw new Error('OIDC configuration is not set.');
    }
    await cognitoAuth.signoutRedirect({
      extraQueryParams: {
        client_id: oidcConfig.client_id,
        logout_uri: oidcConfig.logout_uri,
      },
    });
  };
  const handlePassportSignOut = async () => {
    return client
      .mutate<logOutMutation>({
        mutation: LogOutMutationStatement,
        variables: {},
        context: { noAuth: true },
      })
      .finally(() => {
        client.clearStore();
        window.sessionStorage.clear();
        setAuthObject({
          mode: null,
        });
      });
  };
  const handleGlobalSignOut = async () => {
    writeRoleAndIdToStorage({ orgId: null, role: null, orgAuthId: null });
    setError(null);
    if (authObject.mode === 'cognito') {
      await handleCognitoSignOut();
    } else if (authObject.mode === 'passport') {
      await handlePassportSignOut();
    } else {
      // Do nothing
      client.clearStore();
      setAuthObject({ mode: null });
      setError('Unable to sign out when unauthenticated.');
    }
  };

  const refreshMe = useCallback(async () => {
    try {
      setIsLoading(true);
      DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'refreshMe', { authObject, cognitoAuth });
      const response = await client.query<meQuery>({
        query: MeQueryStatement,
        context: {
          authObject: authObject,
        },
      });
      DEBUG &&
        ClientLogger.debug(
          'MultiAuthProviderInner',
          'refreshMe - meQuery response',
          JSON.stringify({ response, authObject, cognitoAuth }, null, 2)
        );
      setUserState({
        me: response.data.me!,
        isLoggedIn: true,
        ready: true,
      });
      setIsLoading(false);
      return response.data.me!;
    } catch (meQueryError) {
      if (meQueryError instanceof ApolloError && cognitoAuth?.isAuthenticated) {
        errorHandler.handleErrors({ error: meQueryError });
      }
      throw meQueryError;
    }
  }, [authObject, client, cognitoAuth?.isAuthenticated, cognitoAuth?.user]);

  useEffect(() => {
    if (authObject.mode !== null) {
      refreshMe();
    } else {
      setUserState(blankUserState);
    }
  }, [authObject, refreshMe]);

  useEffect(() => {
    setError(cognitoAuth?.error?.message ?? null);
  }, [cognitoAuth?.error?.message]);

  const cognitoSignIn = useCallback(async () => {
    const currentPath = `${role}.${orgId}`; // This can also be used to navigate back to the original page after signing in.
    const encodedState = encodeURIComponent(currentPath);
    DEBUG && ClientLogger.debug('MultiAuthProvider', 'cognitoSignIn', { currentPath, encodedState, cognitoAuth, authObject, authConfig });
    if (cognitoAuth?.isAuthenticated) {
      setAuthObject({
        mode: 'cognito',
        access_token: cognitoAuth.user!.access_token,
        user_payload: cognitoAuth.user!,
      });
      setTokenHolder(cognitoAuth.user!.access_token);
      return true;
    }
    if (authObject.mode !== null) {
      // throw new Error('Already signed.');
    }
    cognitoAuth.signinRedirect({ state: encodedState });
    return true;
  }, [cognitoAuth, authObject, authConfig]);

  const handlePassportSignIn = async (
    username: string | null,
    password: string | null,
    orgId: string | null,
    authConfigId: string | null,
    role: string | null
  ) => {
    setError(null);
    if (authObject.mode !== null) {
      throw new Error('Already signed in using a different method.');
    }
    if (username === null || password === null) {
      throw new Error('Received null `username` or `password`.');
    }

    setIsLoading(true);
    try {
      const data: LoginInput = {
        email: username,
        password: password,
        orgId: orgId,
        authConfigId: authConfigId,
        role: role,
      };

      const response = await clientWithAuth.mutate<loginMutation>({
        mutation: LoginMutationStatement,
        variables: {
          data,
        },
        context: {
          noAuth: true,
        },
      });
      DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'handlePassportSignIn - login mutation response', { response, data });
      if (response.errors) {
        throw new Error(
          response.errors.map((el) => `${el.message} => ${el.locations?.map((el) => `${el.line}:${el.column}`)?.join(',')}`).join('\n')
        );
      }
      if (!response.data || typeof response.data?.login?.accessToken !== 'string') {
        throw new Error('Received invalid data.');
      }
      if (typeof response.data?.login?.accessToken !== 'string') {
        throw new Error('Did not access token.');
      }
      setAuthObject({
        mode: 'passport',
        access_token: response.data.login.accessToken,
      });
      DEBUG &&
        ClientLogger.debug('MultiAuthProviderInner', 'handlePassportSignIn - setting authObject', {
          mode: 'passport',
          access_token: response.data.login.accessToken,
        });
      setTokenHolder(response.data.login.accessToken);
      return response;
    } catch (err) {
      setError(String(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const areTokensExpired = () => {
    if (authObject.mode === 'cognito') {
      return authObject.user_payload.expired!;
    } else if (authObject.mode === 'passport') {
      const now = Date.now() / 1000;
      const decoded = decodeToken(authObject.access_token);
      return decoded.exp < now;
    } else {
      return false;
    }
  };

  const refreshTokens = async () => {
    DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'refreshTokens', { authObject, cognitoAuth });
    if (authObject.mode === 'cognito') {
      if (!cognitoAuth) {
        throw new Error('Cognito auth object is not available.');
      }
      const user = await cognitoAuth.signinSilent();
      if (user) {
        setTokenHolder(user.access_token);
      }

      return user!.access_token;
    } else if (authObject.mode === 'passport') {
      try {
        const response = await client.mutate<refreshTokenMutation>({
          mutation: RefreshTokenMutationStatement,
          context: { noAuth: true },
          errorPolicy: 'all',
        });
        DEBUG &&
          ClientLogger.debug(
            'MultiAuthProviderInner',
            'refreshTokens - refreshToken mutation response',
            JSON.stringify({ response }, null, 2)
          );
        if (typeof response.data?.refreshToken?.accessToken !== 'string') {
          throw new Error('Did not find access token.');
        }
        setAuthObject({
          mode: 'passport',
          access_token: response.data.refreshToken.accessToken,
        });
        setTokenHolder(response.data.refreshToken.accessToken);
        return response.data.refreshToken.accessToken;
      } catch (err) {
        throw err;
      }
    } else {
      return null;
    }
  };

  const tryRefreshTokensAllModes = async () => {
    const user = await cognitoAuth.signinSilent();

    if (user) {
      setTokenHolder(user.access_token);
      setAuthObject({
        mode: 'cognito',
        access_token: user.access_token,
        user_payload: user,
      });
      return user.access_token;
    } else {
      const response = await client.mutate<refreshTokenMutation>({
        mutation: RefreshTokenMutationStatement,
        context: { noAuth: true },
        errorPolicy: 'all',
      });
      if (typeof response.data?.refreshToken?.accessToken !== 'string') {
        throw new Error('Did not find access token.');
      }
      setAuthObject({
        mode: 'passport',
        access_token: response.data.refreshToken.accessToken,
      });
      setTokenHolder(response.data.refreshToken.accessToken);
      return response.data.refreshToken.accessToken;
    }
  };

  useEffect(() => {
    DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'useEffect - inSimulator', { inSimulator });
    if (inSimulator) {
      tryRefreshTokensAllModes();
    }
  }, [inSimulator]);

  const getPersonOrgId = (): string | null => {
    const me = userState.me;
    const personOrgs = me?.personOrgs || [];
    let personOrg = personOrgs.find((po) => po.orgId === me?.currentOrgId);
    DEBUG &&
      ClientLogger.debug('MultiAuthProviderInner', 'getPersonOrgId', {
        personOrgs,
        personOrg,
        currentOrgId: me?.currentOrgId || 'undefined',
      });
    if (personOrgs.length === 0) {
      return null;
    }
    if (personOrg) {
      return personOrg.id;
    } else {
      // take first
      ClientLogger.warning('MultiAuthProviderInner', 'getPersonOrgId - No current org, taking first personOrg');
      personOrg = personOrgs[0];
      if (personOrg) {
        return personOrg.id;
      }
      return null;
    }
  };

  const v: IMultiAuthContext = {
    authObject,

    mode: authObject.mode,
    isAuthenticated: authObject.mode !== null,
    isLoading: isLoading,
    error: error,
    userState: userState,
    $: new UserContextUtils(userState.me),

    cognitoSignIn,
    passportSignIn: handlePassportSignIn,
    globalSignOut: handleGlobalSignOut,

    // Currently `refreshMe` is being used after operations which may change the current user's `Me` object
    refreshMe,

    cognitoAuthObject: cognitoAuth,
    areTokensExpired,
    refreshTokens,
    getPersonOrgId,
    orgId,
    role,
    authConfig,
  };
  GloballyAccessibleMultiAuthContext = v;
  DEBUG && ClientLogger.debug('MultiAuthProviderInner', 'render', { v, oidcConfig, cognitoAuth, veridLoginUI });
  return <MultiAuthContext.Provider value={v}>{children}</MultiAuthContext.Provider>;
};

export const isInSimulator = () => {
  const queryStrings = window.location.search;
  const simSimulationIdQueryString = Util.parseQueryString(queryStrings, SIMULATION_ID_QUERY_PARAM);
  const inSimulator = !!simSimulationIdQueryString;
  return inSimulator;
};

export const MultiAuthProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [authConfig, setAuthConfig] = useState<OrgAuthConfigPublicFragment | null>(null);
  const [authConfigs, setAuthConfigs] = useState<OrgAuthConfigPublicFragment[]>([]);
  const { orgId, role, orgAuthId } = getRoleAndIdFromURLOrStorage();
  const showOrgAndRoleDialog = !orgId || !role;
  const [tempOrgId, setTempOrgId] = useState('hrh');
  const [tempRole, setTempRole] = useState<Role>(Role.ADMIN);
  const toastDispatch = useContext(ToastDispatchContext);
  const [org, setOrg] = useState<OrgPublicFragment | null>(null);
  const inSimulator = isInSimulator();
  const showConfigSelect = authConfigs.length > 1 && !authConfig && !ready;

  const cssClasses = {
    header: {
      width: '100%',
      left: 0,
      right: 0,
      letterSpacing: 0,
    },
    container: {
      maxWidth: '90vw',
      padding: '24px',
    },
    title: {
      fontWeight: 'bold',
      fontSize: '1.5rem',
    },
    containerBtn: {
      width: '100%',
    },
    btnPrimary: {
      padding: `16px 8px`,
    },
  };

  const orgPublic = async (id: string, role: Role): Promise<ApolloQueryResult<orgPublicQuery>> => {
    DEBUG && ClientLogger.debug('useOrgService.orgPublic', `started for id: ${id}, role: ${role}`, { id, role });
    return clientWithoutAuth.query({
      query: orgPublicQueryGQL,
      variables: { id, role },
      fetchPolicy: 'network-only',
    });
  };

  const redirectToOrgAndRole = (orgId: string, role: 'USER' | 'STAFF' | 'ADMIN') => {
    // reload wep page with orgId and role as query parameters
    const url = new URL(window.location.href);
    url.searchParams.set('orgId', orgId);
    url.searchParams.set('role', role);
    DEBUG && ClientLogger.debug('MultiAuthProvider', 'redirectToOrgAndRole', { orgId, role, url });
    // hard reload
    window.location.href = url.toString();
  };

  useEffect(() => {
    DEBUG && console.log('MultiAuthProvider useEffect', { orgId, role });
    if (orgId && role) {
      orgPublic(orgId, role)
        .then((response) => {
          DEBUG && console.log('MultiAuthProvider response: ', response);
          const org = response.data?.orgPublic;
          if (!org) {
            toastDispatch({ severity: SEVERITY.ERROR, msg: `Organization with ID ${orgId} not found` });
            return;
          }
          setOrg(org);
          setAuthConfigs(org.authConfig || []);
          if (orgAuthId) {
            // orgAuthId was set in URL, let's find it
            const foundAuthConfig = org.authConfig?.find((ac) => ac.id === orgAuthId);
            if (foundAuthConfig) {
              setAuthConfig(foundAuthConfig);
              setReady(true);
            } else {
              DEBUG && ClientLogger.debug('MultiAuthProvider', 'useEffect after load - orgAuthId not found', { orgAuthId, org });
              toastDispatch({
                severity: SEVERITY.ERROR,
                msg: `Authentication configuration with ID ${orgAuthId} not found in organization ${orgId}`,
              });
            }
          }
          if (org.authConfig && org.authConfig.length === 1) {
            DEBUG && ClientLogger.debug('MultiAuthProvider', 'useEffect after load ', org.authConfig[0]);
            setAuthConfig(org.authConfig[0]);
            setReady(true);
          }
        })
        .catch((error) => {
          ClientLogger.debug('MultiAuthProvider', 'error: ', error);
          toastDispatch({ severity: SEVERITY.ERROR, msg: `Error fetching organization: ${error.message}` });
        });
    }
  }, [orgId, role, inSimulator]);

  DEBUG &&
    ClientLogger.debug('MultiAuthProvider', 'render', {
      authConfig,
      ready,
      showOrgAndRoleDialog,
      orgId,
      role,
      showConfigSelect,
      orgAuthId,
    });

  if (showOrgAndRoleDialog) {
    return (
      <Dialog open={showOrgAndRoleDialog} onClose={() => {}}>
        <Box sx={{ padding: 2, minWidth: 320 }}>
          <Typography variant="h6" align="center" gutterBottom>
            Error computing orgId and role (this should only happen in local development or prior to AWS migration). Please enter an orgId
            and role to continue.
          </Typography>
          <Box
            component="form"
            sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              if (tempOrgId && tempRole) {
                redirectToOrgAndRole(tempOrgId, tempRole);
              }
            }}
          >
            <TextField
              label="Organization ID"
              variant="outlined"
              value={tempOrgId}
              onChange={(e) => setTempOrgId(e.target.value)}
              required
              fullWidth
            />
            <TextField select label="Role" value={tempRole} onChange={(e) => setTempRole(e.target.value as Role)} required fullWidth>
              <MenuItem value="" disabled>
                Select role
              </MenuItem>
              <MenuItem value="USER">USER</MenuItem>
              <MenuItem value="STAFF">STAFF</MenuItem>
              <MenuItem value="ADMIN">ADMIN</MenuItem>
            </TextField>
            <Button type="submit" variant="contained" color="primary">
              Continue
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  }
  if (showConfigSelect) {
    return (
      <Dialog open={showConfigSelect} onClose={() => {}}>
        <DialogTitle sx={cssClasses.header}>Select Authentication Method for {org?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ padding: 2, minWidth: 320 }}>
            <Grid>
              <Paper elevation={2}>
                {authConfigs.length === 0 && (
                  <Typography align="center" sx={{ marginTop: '16px' }}>
                    No authentication methods available for this organization and role.
                  </Typography>
                )}
                {authConfigs.length > 1 &&
                  authConfigs.map((orgAuthConfig: OrgAuthConfigPublicFragment) => (
                    <Grid container flexDirection="column" alignItems="center" key={orgAuthConfig.id}>
                      <Box my={1} sx={cssClasses.containerBtn}>
                        <Button
                          sx={cssClasses.btnPrimary}
                          variant="contained"
                          fullWidth={true}
                          color="primary"
                          onClick={() => {
                            DEBUG && ClientLogger.debug('MultiAuthProvider', 'Button Clicked', { orgAuthConfig });
                            writeRoleAndIdToStorage({
                              orgId: orgId || null,
                              role: role || null,
                              orgAuthId: orgAuthConfig.id,
                            });
                            setAuthConfig(orgAuthConfig);
                            setReady(true);
                          }}
                        >
                          {orgAuthConfig.name}
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                {/*        
                <Typography align="left" sx={style?.title || cssClasses.title}>
          {(orgAuth && orgAuth.landingPage && orgAuth.landingPage.title) || 'Welcome!'}
        </Typography>

 */}
              </Paper>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!ready || !authConfig || !orgId || !role) {
    return <Loading />;
  }

  DEBUG && ClientLogger.debug('MultiAuthProvider', 'render - ready', { authConfig, orgId, role });

  const oidcConfig = convertOrgAuthConfigPublicFragmentToIoidcConfig(authConfig);

  return (
    // We need to use the `key` prop to force the AuthProvider to re-render when oidcConfig changes.
    // This is a workaround for the issue where AuthProvider does not update its value when oidcConfig changes.
    // https://github.com/authts/oidc-client-ts/issues/1027
    // https://github.com/authts/react-oidc-context/issues/930
    <AuthProvider key={JSON.stringify(oidcConfig)} {...oidcConfig}>
      <ApolloProvider client={clientWithAuth}>
        <MultiAuthProviderInner authConfig={authConfig} orgId={orgId} role={role}>
          <>{children}</>
        </MultiAuthProviderInner>
      </ApolloProvider>
    </AuthProvider>
  );
};

export const useMultiAuth = () => {
  const context = useContext(MultiAuthContext);
  if (context === null) {
    throw new Error('`useMultiAuth` must be used inside `MultiAuthProvider`.');
  }
  return context;
};

export const useVeridApi = () => {
  const context = useContext(MultiAuthContext);
  if (context === null) {
    throw new Error('`useMultiApi` must be used inside `MultiAuthProvider`.');
  }
  const apiFunctions = useRef({
    clientWithAuth,
    clientWithoutAuth,

    query<T, TVariables extends OperationVariables = OperationVariables>(options: QueryOptions<TVariables>) {
      return clientWithAuth.query<T>({
        errorPolicy: 'all',
        ...options,
        context: { ...context.userState, ...options.context },
      });
    },

    queryWithoutContext<T, TVariables extends OperationVariables = OperationVariables>(options: QueryOptions<TVariables>) {
      return clientWithAuth.query<T>({ errorPolicy: 'all', ...options });
    },

    mutate<T, TVariables extends OperationVariables = OperationVariables>(options: MutationOptions<T, TVariables>) {
      return clientWithAuth.mutate<T, TVariables>({
        errorPolicy: 'all',
        ...options,
        context: {
          ...context.userState,
          ...options.context,
        },
      });
    },

    useMutation<T, TVariables extends OperationVariables = OperationVariables>(
      mutation: DocumentNode,
      options?: MutationHookOptions<T, TVariables>
    ) {
      return useMutation<T, TVariables>(mutation, {
        client: clientWithAuth,
        ...options,
        context: { ...context.userState, authObject: context.authObject, ...options?.context },
      });
    },

    useQuery<TData, TVariables extends OperationVariables = OperationVariables>(
      query: DocumentNode,
      options?: QueryHookOptions<TData, TVariables>
    ) {
      return useQuery<TData, TVariables>(query, {
        client: clientWithAuth,
        ...options,
        context: { ...context.userState, authObject: context.authObject, ...options?.context },
      });
    },

    refreshMe: context.refreshMe,
    cognitoSignIn: context.cognitoSignIn,
    orgId: context.orgId,
    role: context.role,
    authConfig: context.authConfig,
  });
  return apiFunctions.current;
};
